import { useState, type FormEvent } from 'react';
import { Link, NavLink, useLocation, useParams, useSearchParams } from 'react-router';
import { api, ApiError } from '../../api/client';
import type { ClientDetail, ModuleSelection } from '@pixerito/api-client';
import { DashboardView } from '../../components/DashboardView';
import { ModuleToggles } from '../../components/ModuleToggles';
import { Dialog, ErrorNotice, Field, Icon, Loading, Switch, Toast, Watermark } from '../../components/ui';
import { useAsync, useToast } from '../../lib/hooks';
import { formatDate, relativeDays } from '../../lib/format';
import { paramsFromRange, queryFromParams } from '../../lib/period';
import { AdminDeliverables } from './AdminDeliverables';
import { AdminVideos } from './AdminVideos';
import { MonthlyEntryForm } from './MonthlyEntry';
import { generatePassword } from './NewClient';

const TABS = [
  { key: 'resumo', label: 'Resumo e módulos' },
  { key: 'lancamento', label: 'Lançamento mensal' },
  { key: 'entregaveis', label: 'Entregáveis' },
  { key: 'videos', label: 'Vídeos do mês' },
  { key: 'previa', label: 'Ver como cliente' },
] as const;

export function ClientDetailPage() {
  const { id = '', tab = 'resumo' } = useParams();
  const location = useLocation();
  const toast = useToast();
  const { data: client, setData, error, loading, reload } = useAsync(() => api.client(id), [id]);
  const created = (location.state as { created?: { email: string; password: string } } | null)?.created;

  if (loading && !client) return <Loading />;
  if (error) return <ErrorNotice message={error} onRetry={reload} />;
  if (!client) return null;

  const enabled = (t: string) => client.modules.some((m) => m.type === t && m.enabled);
  const videoRecurring = client.modules.some((m) => m.type === 'video' && m.enabled && m.isRecurring);
  const tabs = TABS.filter((t) => t.key !== 'videos' || videoRecurring);

  return (
    <>
      <Link to="/admin" className="back-link">
        <Icon name="arrowLeft" size={15} />
        Clientes
      </Link>
      <header className="client-head">
        <div className="stack" style={{ gap: 6, minWidth: 0 }}>
          <p className="eyebrow">&lt;_Cliente/&gt;</p>
          <h1 className="t-display">{client.name}</h1>
          <p className="t-sub">
            {client.isActive ? 'Ativo' : 'Desativado'} · cliente desde {formatDate(client.createdAt)}
            {client.users[0] && ` · último acesso ${relativeDays(client.users[0].lastLoginAt)}`}
          </p>
        </div>
      </header>

      {created && tab === 'resumo' && (
        <CreatedNotice email={created.email} password={created.password} onCopied={() => toast.show('Acesso copiado')} />
      )}

      <nav className="tabs admin-tabs" aria-label="Seções do cliente">
        {tabs.map((t) => (
          <NavLink
            key={t.key}
            to={t.key === 'resumo' ? `/admin/clientes/${id}` : `/admin/clientes/${id}/${t.key}`}
            end
            className={({ isActive }) => (isActive || (t.key === 'resumo' && tab === 'resumo') ? 'active' : '')}
          >
            {t.label}
          </NavLink>
        ))}
      </nav>

      {tab === 'resumo' && (
        <SummaryTab
          client={client}
          onSaved={(c, msg) => {
            setData(c);
            toast.show(msg);
          }}
        />
      )}
      {tab === 'lancamento' && <MonthlyEntryForm client={client} onSaved={toast.show} />}
      {tab === 'entregaveis' &&
        (enabled('brand') || enabled('video') ? (
          <AdminDeliverables client={client} onToast={toast.show} />
        ) : (
          <ModuleOff what="Identidade Visual ou Vídeo" clientId={id} />
        ))}
      {tab === 'videos' &&
        (videoRecurring ? <AdminVideos client={client} onToast={toast.show} /> : <ModuleOff what="Vídeo recorrente" clientId={id} />)}
      {tab === 'previa' && <PreviewTab clientId={id} />}

      <Toast message={toast.message} />
    </>
  );
}

function ModuleOff({ what, clientId }: { what: string; clientId: string }) {
  return (
    <div className="empty">
      <Watermark />
      <p className="t-h3">Módulo desativado</p>
      <p className="muted t-small">Ative {what} em Resumo e módulos para usar esta seção.</p>
      <div>
        <Link to={`/admin/clientes/${clientId}`} className="btn btn--ghost btn--sm">
          Ir para módulos
        </Link>
      </div>
    </div>
  );
}

function CreatedNotice({ email, password, onCopied }: { email: string; password: string; onCopied: () => void }) {
  const text = `Seu acesso ao Pixerito Social\nLink: ${window.location.origin}/login\nE-mail: ${email}\nSenha provisória: ${password}\nNo primeiro acesso, você cria uma senha sua.`;
  return (
    <div className="notice" style={{ marginBottom: 24, borderColor: 'var(--highlight)' }}>
      <div className="stack" style={{ gap: 8, minWidth: 0 }}>
        <p style={{ fontWeight: 600 }}>Cliente cadastrado. Envie o acesso:</p>
        <p className="mono t-small" style={{ overflowWrap: 'anywhere' }}>
          {email} · {password}
        </p>
        <p className="t-caption">A senha não fica visível depois. Se precisar, gere uma nova em Acesso ao painel.</p>
        <div>
          <button
            className="btn btn--ghost btn--sm"
            onClick={() => {
              navigator.clipboard
                ?.writeText(text)
                .then(onCopied)
                .catch(() => undefined);
            }}
          >
            <Icon name="copy" size={15} />
            Copiar mensagem de acesso
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Resumo e módulos ──

function SummaryTab({ client, onSaved }: { client: ClientDetail; onSaved: (c: ClientDetail, msg: string) => void }) {
  const [form, setForm] = useState({
    name: client.name,
    contactName: client.contactName ?? '',
    contactEmail: client.contactEmail ?? '',
    contactPhone: client.contactPhone ?? '',
    notes: client.notes ?? '',
    isActive: client.isActive,
  });
  const [modules, setModules] = useState<ModuleSelection[]>(
    client.modules.map((m) => ({ type: m.type, enabled: m.enabled, isRecurring: m.isRecurring })),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<'data' | 'modules' | null>(null);
  const [resetFor, setResetFor] = useState<{ id: string; email: string } | null>(null);
  const modulesChanged =
    JSON.stringify(modules) !==
    JSON.stringify(client.modules.map((m) => ({ type: m.type, enabled: m.enabled, isRecurring: m.isRecurring })));

  const setF = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [k]: e.target.value });

  async function saveData(e: FormEvent) {
    e.preventDefault();
    setBusy('data');
    setErrors({});
    try {
      onSaved(await api.updateClient(client.id, form), 'Dados salvos');
    } catch (err) {
      if (err instanceof ApiError) setErrors({ ...err.fields, _: err.message });
    } finally {
      setBusy(null);
    }
  }

  async function saveModules() {
    setBusy('modules');
    try {
      onSaved(await api.setModules(client.id, modules), 'Módulos atualizados');
    } catch (err) {
      if (err instanceof ApiError) setErrors({ modules: err.message });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      className="grid-2"
      style={{ alignItems: 'start', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))', gap: 24 }}
    >
      <section className="card stack">
        <div className="card__head" style={{ marginBottom: 0 }}>
          <div>
            <h2 className="t-h3">Serviços contratados</h2>
            <p className="muted t-small">Desativar esconde o módulo do cliente. Os dados ficam guardados.</p>
          </div>
        </div>
        {errors.modules && <span className="field__error">{errors.modules}</span>}
        <ModuleToggles value={modules} onChange={setModules} />
        <div className="row">
          <button className="btn btn--action btn--sm" disabled={!modulesChanged || busy === 'modules'} onClick={saveModules}>
            {busy === 'modules' ? 'Salvando…' : 'Salvar módulos'}
          </button>
          {modulesChanged && (
            <button
              className="btn btn--quiet btn--sm"
              onClick={() => setModules(client.modules.map((m) => ({ type: m.type, enabled: m.enabled, isRecurring: m.isRecurring })))}
            >
              Desfazer
            </button>
          )}
        </div>
      </section>

      <div className="stack-lg">
        <form className="card stack" onSubmit={saveData} noValidate>
          <h2 className="t-h3">Dados do cliente</h2>
          {errors._ && <div className="notice notice--error">{errors._}</div>}
          <Field label="Nome da empresa" htmlFor="e-name" error={errors.name}>
            <input id="e-name" className="input" value={form.name} onChange={setF('name')} />
          </Field>
          <div className="grid-2">
            <Field label="Contratante" htmlFor="e-contact">
              <input id="e-contact" className="input" value={form.contactName} onChange={setF('contactName')} />
            </Field>
            <Field label="Telefone / WhatsApp" htmlFor="e-phone">
              <input id="e-phone" className="input" value={form.contactPhone} onChange={setF('contactPhone')} />
            </Field>
          </div>
          <Field label="E-mail do contratante" htmlFor="e-email" error={errors.contactEmail}>
            <input id="e-email" className="input" type="email" value={form.contactEmail} onChange={setF('contactEmail')} />
          </Field>
          <Field label="Observações internas" htmlFor="e-notes">
            <textarea id="e-notes" className="textarea" value={form.notes} onChange={setF('notes')} />
          </Field>
          <Switch
            checked={form.isActive}
            onChange={(v) => setForm({ ...form, isActive: v })}
            label="Cliente ativo"
            description="Desativado, o cliente não consegue entrar no painel."
          />
          <div>
            <button className="btn btn--action btn--sm" type="submit" disabled={busy === 'data'}>
              {busy === 'data' ? 'Salvando…' : 'Salvar dados'}
            </button>
          </div>
        </form>

        <section className="card stack">
          <h2 className="t-h3">Acesso ao painel</h2>
          {client.users.length === 0 ? (
            <p className="muted t-small">Nenhum login criado.</p>
          ) : (
            client.users.map((u) => (
              <div key={u.id} className="row" style={{ justifyContent: 'space-between' }}>
                <div className="stack" style={{ gap: 0, minWidth: 0 }}>
                  <span style={{ fontWeight: 500 }}>{u.name}</span>
                  <span className="t-caption" style={{ overflowWrap: 'anywhere' }}>
                    {u.email} · último acesso {relativeDays(u.lastLoginAt)}
                  </span>
                </div>
                <button className="btn btn--ghost btn--sm" onClick={() => setResetFor({ id: u.id, email: u.email })}>
                  Nova senha
                </button>
              </div>
            ))
          )}
        </section>
      </div>

      {resetFor && (
        <ResetPasswordDialog
          clientId={client.id}
          user={resetFor}
          onClose={() => setResetFor(null)}
          onDone={() => onSaved(client, 'Senha alterada')}
        />
      )}
    </div>
  );
}

function ResetPasswordDialog({
  clientId,
  user,
  onClose,
  onDone,
}: {
  clientId: string;
  user: { id: string; email: string };
  onClose: () => void;
  onDone: () => void;
}) {
  const [password, setPassword] = useState(generatePassword());
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.resetPassword(clientId, user.id, password);
      setSaved(true);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível salvar.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title="Nova senha" onClose={onClose}>
      <p className="muted t-small">
        Para {user.email}. A senha antiga e as sessões abertas deixam de funcionar na hora. No primeiro acesso, o cliente cria uma senha
        dele.
      </p>
      <Field label="Senha" htmlFor="new-pass" error={error}>
        <div className="password-box">
          <input
            id="new-pass"
            className="input"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setSaved(false);
            }}
          />
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => {
              setPassword(generatePassword());
              setSaved(false);
            }}
          >
            Gerar
          </button>
        </div>
      </Field>
      {saved ? (
        <div className="notice">
          <span>
            Senha salva. Envie <b className="mono">{password}</b> para o cliente.
          </span>
        </div>
      ) : null}
      <div className="row">
        {saved ? (
          <button className="btn btn--action" onClick={onClose}>
            Fechar
          </button>
        ) : (
          <button className="btn btn--action" onClick={save} disabled={busy}>
            {busy ? 'Salvando…' : 'Salvar senha'}
          </button>
        )}
      </div>
    </Dialog>
  );
}

// ── Prévia ──

function PreviewTab({ clientId }: { clientId: string }) {
  const [params, setParams] = useSearchParams();
  const query = queryFromParams(params);
  const { data, error, loading, reload } = useAsync(
    () => api.clientDashboard(clientId, query),
    [clientId, query?.period, query?.from, query?.to],
  );
  if (loading && !data) return <Loading />;
  if (error) return <ErrorNotice message={error} onRetry={reload} />;
  if (!data) return null;
  return (
    <>
      <div className="preview-banner">
        <span className="tag tag--hl">Prévia</span>
        <span className="muted t-small">É exatamente isso que o cliente vê ao entrar.</span>
      </div>
      <div className="preview-frame" style={{ opacity: loading ? 0.6 : 1 }}>
        <DashboardView data={data} onRangeChange={(r) => setParams(paramsFromRange(r))} />
      </div>
    </>
  );
}
